import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { store } from "../stores/Store";
import { formatBRL } from "../utils";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { FormattedHtmlText } from "../components/FormattedHtmlText";
import { Produto } from "../types/store";

export const Product: React.FC = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Produto | null>(
    store.getProductsById(Number(id))
  );
  const [loading, setLoading] = useState(!product);
  const [showModal, setShowModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!product && id) {
      setLoading(true);
      store
        .loadProdutoById(parseInt(id))
        .then((data) => {
          if (data) {
            setProduct(data);
            setLoading(false);
          } else {
            setProduct(null);
            setLoading(false);
          }
        })
        .catch(() => {
          setProduct(null);
          setLoading(false);
        });
    }
  }, [id, product]);

  if (loading) {
    return <div>Carregando produto...</div>;
  }

  if (!product) {
    return <div>Produto não encontrado</div>;
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    store.addToCart(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCheckout = () => {
    navigate("/carrinho");
  };

  return (
    <div className="container-fluid ctn_produto bg_branco">
      <div className="container ctn_default">
        <div className="row">
          <div className="col-md-12">
            <div className="ctn_slider">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                loop={true}
                className="product-swiper"
              >
                {product.fotoCapaUrl && (
                  <SwiperSlide>
                    <img
                      src={product.fotoCapaUrl}
                      alt={product.nome}
                      style={{ cursor: "pointer" }}
                      onClick={() => setModalImageUrl(product.fotoCapaUrl)}
                    />
                  </SwiperSlide>
                )}
                {Array.isArray(product.fotoProdutosUrls) &&
                  product.fotoProdutosUrls.map((fotoUrl, index) => (
                    <SwiperSlide key={fotoUrl ? `${fotoUrl}-${index}` : `foto-${index}`}>
                      <img
                        src={fotoUrl}
                        alt={`${product.nome} - Foto ${index + 1}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setModalImageUrl(fotoUrl)}
                      />
                    </SwiperSlide>
                  ))}
              </Swiper>
            </div>

            {/* Modal de Imagem Ampliada */}
            {modalImageUrl && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 3000,
                }}
                onClick={() => setModalImageUrl(null)}
              >
                <div
                  style={{
                    position: "relative",
                    background: "transparent",
                    padding: 0,
                    borderRadius: 8,
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: 36,
                      height: 36,
                      fontSize: 22,
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                    }}
                    onClick={() => setModalImageUrl(null)}
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                  <img
                    src={modalImageUrl}
                    alt="Produto ampliado"
                    style={{
                      display: "block",
                      maxWidth: "80vw",
                      maxHeight: "80vh",
                      margin: "0 auto",
                      borderRadius: 8,
                      background: "#fff",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Informacoes */}
            <div className="ctn_infos">
              <h1 className="titulo_25 titulo_700 txt_preto mg-bt_20">
                {product.nome}
              </h1>

              <p>{product.descricao}</p>


              {product.autores && (<div className="mg-bt_10">
                <h2 className="titulo_15 titulo_700 txt_preto">Autores</h2>
                <p>{product.autores}</p>
              </div>)
              }


              <div className="mg-bt_10">
                <h2 className="titulo_15 titulo_700 txt_preto">
                  Informações Adicionais
                </h2>
                <FormattedHtmlText htmlText={product.informacaoAdicional} />
              </div>

              {product.peso > 0 && (
                <div className="mg-bt_10">
                  <h2 className="titulo_15 titulo_700 txt_preto">Peso</h2>
                  <p>{product.peso} kg</p>
                </div>
              )}

              <div className="mg-bt_10">
                <h3 className="preco titulo_25 titulo_700 txt_cor-id-1">
                  {product.desconto > 0 && (
                    <span className="desconto">{formatBRL(product.preco)}</span>
                  )}
                  {formatBRL(product.preco - product.desconto)}
                </h3>
              </div>

              {product.quantidadeEstoque > 0 ? (
                <button className="bt bt_carrinho" onClick={handleAddToCart}>
                  Adicionar
                </button>
              ) : (
                <div
                  style={{
                    color: "#dc3545",
                    fontWeight: "bold",
                    textAlign: "center",
                    marginTop: 8,
                  }}
                >
                  ESGOTADO
                </div>
              )}
            </div>
            {/* Modal Popup */}
            {showModal && (
              <div className="custom-modal-overlay">
                <div className="custom-modal">
                  <p className="custom-modal-title">
                    Produto adicionado ao carrinho!
                  </p>
                  <div className="custom-modal-actions">
                    <button className="bt bt_center" onClick={handleCloseModal}>
                      Continuar Comprando
                    </button>
                    <button
                      className="bt bt_center bt_carrinho"
                      onClick={handleCheckout}
                    >
                      Finalizar Pedido
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
